import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, brl, fmtDate } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  RefreshCw, TrendingUp, TrendingDown, ArrowRight, AlertTriangle,
  Landmark, Receipt, HandCoins, BookOpen, PieChart, Lightbulb, LineChart,
  Wallet, ExternalLink,
} from "lucide-react";

function today() { return new Date().toISOString().slice(0, 10); }
function firstDayOfMonth() {
  const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}
function lastDayOfMonth() {
  const d = new Date(); const l = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return `${l.getFullYear()}-${String(l.getMonth() + 1).padStart(2, "0")}-${String(l.getDate()).padStart(2, "0")}`;
}
const REGIMES = [
  { key: "caixa", label: "Caixa" },
  { key: "competencia", label: "Competência" },
];

function Kpi({ label, valor, sub, tone = "neutral", icon: Icon, testid }) {
  const cls = {
    danger: "bg-[#F5E4DD] text-[#7c3a20] border-[#D4A652]/25",
    warn: "bg-[#FFF4D6] text-[#8B6A1F] border-[#8B6A1F]/25",
    ok: "bg-[#EAF0EA] text-[#083A6B] border-[#083A6B]/20",
    info: "bg-[#EDE9FE] text-[#5B21B6] border-[#5B21B6]/25",
    neutral: "bg-[#FFFFFF] text-stone-800 border-stone-200",
  }[tone];
  return (
    <div data-testid={testid} className={`border rounded-lg px-4 py-3 ${cls}`}>
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest opacity-70">
        {Icon ? <Icon size={12} /> : null} {label}
      </div>
      <div className="font-serif text-xl tabular-nums leading-tight mt-1">
        {valor === null || valor === undefined || Number.isNaN(valor) ? "—" : brl(valor)}
      </div>
      {sub && <div className="text-[11px] opacity-75 mt-0.5">{sub}</div>}
    </div>
  );
}

function SectionHeader({ icon: Icon, title, subtitle, link, linkLabel }) {
  return (
    <div className="flex items-end justify-between gap-3 mb-3 mt-8">
      <div>
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-stone-500">
          {Icon ? <Icon size={13} className="text-[#083A6B]" /> : null} Financeiro
        </div>
        <h2 className="font-serif text-2xl text-stone-900">{title}</h2>
        {subtitle && <p className="text-sm text-stone-600 mt-1">{subtitle}</p>}
      </div>
      {link && (
        <Link to={link} className="shrink-0">
          <Button size="sm" variant="outline">
            {linkLabel || "Abrir módulo"} <ExternalLink size={13} className="ml-1" />
          </Button>
        </Link>
      )}
    </div>
  );
}

function EmptyBox({ testid, children }) {
  return (
    <div data-testid={testid}
      className="bg-[#FFFFFF] border border-dashed border-stone-300 rounded-lg p-6 text-center text-sm text-stone-600">
      {children}
    </div>
  );
}

export default function DashboardFinanceiro() {
  const [pousadas, setPousadas] = useState([]);
  const [me, setMe] = useState(null);
  const [filtros, setFiltros] = useState({
    de: firstDayOfMonth(),
    ate: lastDayOfMonth(),
    pousada_id: "all",
    regime: "caixa",
  });

  // seções independentes → não trava a página inteira
  const [dre, setDre] = useState({ data: null, loading: true, error: null });
  const [cpResumo, setCpResumo] = useState({ data: null, loading: true, error: null });
  const [cr, setCr] = useState({ data: [], loading: true, error: null });
  const [cfs, setCfs] = useState({ data: [], loading: true, error: null });
  const [mfs, setMfs] = useState({ data: [], loading: true, error: null });
  const [rent, setRent] = useState({ data: null, loading: true, error: null });
  const [participacao, setParticipacao] = useState({ data: null, loading: true, error: null });
  const [projecao, setProjecao] = useState({ data: null, loading: true, error: null });
  const [investir, setInvestir] = useState({ data: null, loading: true, error: null });

  useEffect(() => {
    (async () => {
      try {
        const [po, u] = await Promise.all([
          api.get("/pousadas-list").catch(() => ({ data: [] })),
          api.get("/auth/me").catch(() => ({ data: null })),
        ]);
        setPousadas(Array.isArray(po.data) ? po.data.filter((x) => x.id) : []);
        setMe(u.data);
      } catch { /* silencioso */ }
    })();
  }, []);

  const pousadaMap = useMemo(() => Object.fromEntries(pousadas.map((p) => [p.id, p.nome])), [pousadas]);
  const pousadaParam = filtros.pousada_id !== "all" ? filtros.pousada_id : undefined;

  const load = useCallback(async () => {
    // DRE (F12.1/F12.2)
    setDre((s) => ({ ...s, loading: true, error: null }));
    api.get("/dre/resumo", { params: { de: filtros.de, ate: filtros.ate, pousada_id: pousadaParam, regime: filtros.regime } })
      .then((r) => setDre({ data: r.data, loading: false, error: null }))
      .catch((e) => setDre({ data: null, loading: false, error: e?.response?.status === 401 ? "auth" : "erro" }));

    // Contas a Pagar resumo (F5)
    setCpResumo((s) => ({ ...s, loading: true, error: null }));
    api.get("/contas-pagar/resumo", { params: { pousada_id: pousadaParam, data_inicio: filtros.de, data_fim: filtros.ate } })
      .then((r) => setCpResumo({ data: r.data, loading: false, error: null }))
      .catch((e) => setCpResumo({ data: null, loading: false, error: e?.response?.status === 401 ? "auth" : "erro" }));

    // Contas a Receber (F3) — filtra por vencimento no período
    setCr((s) => ({ ...s, loading: true, error: null }));
    api.get("/contas-receber", {
      params: {
        vencimento_de: filtros.de, vencimento_ate: filtros.ate,
        pousada_id: pousadaParam, is_cancelada: false, limit: 1000,
      },
    })
      .then((r) => setCr({ data: Array.isArray(r.data) ? r.data : [], loading: false, error: null }))
      .catch((e) => setCr({ data: [], loading: false, error: e?.response?.status === 401 ? "auth" : "erro" }));

    // Contas Financeiras (F4) — listar todas ativas
    setCfs((s) => ({ ...s, loading: true, error: null }));
    api.get("/contas-financeiras", { params: { ativo: true, pousada_id: pousadaParam } })
      .then((r) => setCfs({ data: Array.isArray(r.data) ? r.data : [], loading: false, error: null }))
      .catch((e) => setCfs({ data: [], loading: false, error: e?.response?.status === 401 ? "auth" : "erro" }));

    // Movimentos Financeiros (F6)
    setMfs((s) => ({ ...s, loading: true, error: null }));
    api.get("/movimentos-financeiros", {
      params: {
        de: filtros.de, ate: filtros.ate, pousada_id: pousadaParam,
        status: "efetivo",
      },
    })
      .then((r) => setMfs({ data: Array.isArray(r.data) ? r.data : [], loading: false, error: null }))
      .catch((e) => setMfs({ data: [], loading: false, error: e?.response?.status === 401 ? "auth" : "erro" }));

    // Rentabilidade — ranking por resultado (F12.3)
    setRent((s) => ({ ...s, loading: true, error: null }));
    api.get("/rentabilidade/ranking", {
      params: {
        de: filtros.de, ate: filtros.ate, regime: filtros.regime,
        metrica: "resultado", ordem: "desc", incluir_sem_pousada: false,
      },
    })
      .then((r) => setRent({ data: r.data, loading: false, error: null }))
      .catch((e) => setRent({ data: null, loading: false, error: e?.response?.status === 401 ? "auth" : "erro" }));

    // Participação (F12.3)
    setParticipacao((s) => ({ ...s, loading: true, error: null }));
    api.get("/rentabilidade/participacao", { params: { de: filtros.de, ate: filtros.ate, regime: filtros.regime } })
      .then((r) => setParticipacao({ data: r.data, loading: false, error: null }))
      .catch((e) => setParticipacao({ data: null, loading: false, error: e?.response?.status === 401 ? "auth" : "erro" }));

    // Projeção (F10) — resumo horizonte 30 dias
    setProjecao((s) => ({ ...s, loading: true, error: null }));
    api.get("/projecoes-financeiras/resumo", {
      params: { horizonte_dias: 30, pousada_id: pousadaParam },
    })
      .then((r) => setProjecao({ data: r.data, loading: false, error: null }))
      .catch((e) => setProjecao({ data: null, loading: false, error: e?.response?.status === 401 ? "auth" : "erro" }));

    // Podemos Investir (F11) — resumo horizonte 90 dias
    setInvestir((s) => ({ ...s, loading: true, error: null }));
    api.get("/podemos-investir/resumo", {
      params: { horizonte_dias: 90, pousada_id: pousadaParam },
    })
      .then((r) => setInvestir({ data: r.data, loading: false, error: null }))
      .catch((e) => setInvestir({ data: null, loading: false, error: e?.response?.status === 401 ? "auth" : "erro" }));
  }, [filtros, pousadaParam]);

  useEffect(() => { load(); }, [load]);

  // Totais derivados da listagem CR (só somas neutras — não redefine regra F3)
  const crTotais = useMemo(() => {
    let total = 0, recebido = 0, saldo = 0, count = 0;
    (cr.data || []).forEach((c) => {
      if (c.is_cancelada) return;
      total += Number(c.valor_final || 0);
      recebido += Number(c.valor_recebido || 0);
      saldo += Number(c.saldo || 0);
      count += 1;
    });
    return { total, recebido, saldo, count };
  }, [cr.data]);

  const mfTotais = useMemo(() => {
    let e = 0, s = 0;
    (mfs.data || []).forEach((m) => {
      if (m.status !== "efetivo") return;
      const v = Number(m.valor || 0);
      if (m.tipo === "entrada") e += v;
      else if (m.tipo === "saida") s += v;
    });
    return { entradas: e, saidas: s, resultado: e - s };
  }, [mfs.data]);

  const anyAuth = [dre, cpResumo, cr, cfs, mfs, rent, participacao, projecao, investir].some(
    (s) => s.error === "auth"
  );

  return (
    <div data-testid="page-dashboard-financeiro">
      <PageHeader
        eyebrow="Financeiro · Visão consolidada"
        title="Dashboard Financeiro"
        description="Visão executiva unificando F2/F3/F4/F5/F6, DRE (F12.1/F12.2), Rentabilidade (F12.3), Projeções (F10) e Podemos Investir? (F11). Apenas leitura."
        actions={
          <Button variant="outline" size="sm" onClick={load} data-testid="btn-refresh-dashboard">
            <RefreshCw size={14} /> Atualizar
          </Button>
        }
      />

      {anyAuth && (
        <div className="bg-[#F5E4DD] text-[#7c3a20] border border-[#D4A652]/25 rounded-lg px-4 py-2 text-sm mb-4">
          Sessão expirada em uma ou mais consultas. Faça login novamente.
        </div>
      )}

      {/* Filtros globais */}
      <div className="flex flex-wrap items-end gap-3 mb-6">
        <div>
          <Label className="text-[11px] uppercase tracking-widest text-stone-500">De</Label>
          <Input type="date" data-testid="filter-financeiro-de" value={filtros.de}
            onChange={(e) => setFiltros({ ...filtros, de: e.target.value })} className="w-[150px]" />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-widest text-stone-500">Até</Label>
          <Input type="date" data-testid="filter-financeiro-ate" value={filtros.ate}
            onChange={(e) => setFiltros({ ...filtros, ate: e.target.value })} className="w-[150px]" />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-widest text-stone-500">Pousada</Label>
          <Select value={filtros.pousada_id} onValueChange={(v) => setFiltros({ ...filtros, pousada_id: v })}>
            <SelectTrigger data-testid="filter-financeiro-pousada" className="w-[200px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              {pousadas.map((p) => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-widest text-stone-500">Regime (DRE / Rentabilidade)</Label>
          <Select value={filtros.regime} onValueChange={(v) => setFiltros({ ...filtros, regime: v })}>
            <SelectTrigger data-testid="filter-financeiro-regime" className="w-[180px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {REGIMES.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* KPIs Executivos consolidados */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <Kpi testid="kpi-financeiro-receita" label={`Receita (${filtros.regime === "caixa" ? "caixa" : "competência"})`}
          valor={dre.data?.receita_liquida ?? dre.data?.receita_bruta ?? null}
          sub={dre.loading ? "Carregando..." : (dre.error ? "Indisponível" : "DRE oficial")}
          tone="ok" icon={TrendingUp} />
        <Kpi testid="kpi-financeiro-despesa" label="Despesa"
          valor={dre.data ? (Number(dre.data.custos_diretos || 0) + Number(dre.data.despesas_operacionais || 0)) : null}
          sub={dre.loading ? "Carregando..." : (dre.error ? "Indisponível" : "Custos + Despesas op.")}
          tone="danger" icon={TrendingDown} />
        <Kpi testid="kpi-financeiro-resultado" label="Resultado"
          valor={dre.data?.resultado_operacional ?? null}
          sub={dre.loading ? "Carregando..." : (dre.error ? "Indisponível" : "Res. operacional DRE")}
          tone={((dre.data?.resultado_operacional ?? 0) >= 0) ? "ok" : "danger"} icon={ArrowRight} />
        <Kpi testid="kpi-financeiro-cr-saldo" label="Contas a Receber (saldo)"
          valor={cr.loading ? null : crTotais.saldo}
          sub={cr.loading ? "Carregando..." : `${crTotais.count} contas no período`}
          tone="warn" icon={HandCoins} />
        <Kpi testid="kpi-financeiro-cp-vencidas" label="CP Vencidas"
          valor={cpResumo.data?.vencidas?.valor ?? null}
          sub={cpResumo.loading ? "Carregando..." : `${cpResumo.data?.vencidas?.count ?? 0} contas`}
          tone="danger" icon={Receipt} />
        <Kpi testid="kpi-financeiro-cp-pendente" label="CP Pendente"
          valor={cpResumo.data?.total_pendente?.valor ?? null}
          sub={cpResumo.loading ? "Carregando..." : `${cpResumo.data?.total_pendente?.count ?? 0} contas`}
          tone="neutral" icon={Receipt} />
      </div>

      {/* DRE (F12.1/F12.2) */}
      <section data-testid="section-dre">
        <SectionHeader icon={PieChart}
          title={`DRE — regime ${filtros.regime === "caixa" ? "Caixa" : "Competência"}`}
          subtitle="Estrutura oficial retornada pelo backend. Frontend não recalcula."
        />
        {dre.loading ? (
          <div className="text-stone-500 text-sm">Carregando DRE...</div>
        ) : dre.error ? (
          <EmptyBox testid="dre-erro">Não foi possível carregar o DRE.</EmptyBox>
        ) : !dre.data ? (
          <EmptyBox testid="dre-vazio">DRE indisponível no momento.</EmptyBox>
        ) : (
          <div className="bg-[#FFFFFF] border border-stone-200 rounded-lg p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 text-sm">
              <div className="flex justify-between"><span>Receita bruta</span><b className="tabular-nums">{brl(dre.data.receita_bruta)}</b></div>
              <div className="flex justify-between"><span>Deduções de receita</span><b className="tabular-nums">{brl(dre.data.deducoes_receita)}</b></div>
              <div className="flex justify-between border-t border-stone-200 pt-1 mt-1"><span>Receita líquida</span><b className="tabular-nums text-[#083A6B]">{brl(dre.data.receita_liquida)}</b></div>
              <div className="flex justify-between"><span>Custos diretos</span><b className="tabular-nums">{brl(dre.data.custos_diretos)}</b></div>
              <div className="flex justify-between"><span>Lucro bruto</span><b className="tabular-nums">{brl(dre.data.lucro_bruto)}</b></div>
              <div className="flex justify-between"><span>Despesas operacionais</span><b className="tabular-nums">{brl(dre.data.despesas_operacionais)}</b></div>
              <div className="flex justify-between border-t border-stone-200 pt-1 mt-1 md:col-span-2">
                <span className="font-medium">Resultado operacional</span>
                <b className={`tabular-nums text-lg ${Number(dre.data.resultado_operacional || 0) >= 0 ? "text-[#083A6B]" : "text-[#D4A652]"}`}>
                  {brl(dre.data.resultado_operacional)}
                </b>
              </div>
            </div>
            <div className="mt-3 text-[11px] text-stone-500">
              Período: {dre.data.periodo?.de || filtros.de} → {dre.data.periodo?.ate || filtros.ate} · Regime: {filtros.regime}
              {dre.data.total_lancamentos !== undefined && ` · ${dre.data.total_lancamentos} lançamentos`}
            </div>
          </div>
        )}
      </section>

      {/* Rentabilidade (F12.3) */}
      <section data-testid="section-rentabilidade">
        <SectionHeader icon={LineChart}
          title="Rentabilidade por pousada"
          subtitle="Ordenação matemática por resultado. Sem score ou avaliação subjetiva. `SEM_POUSADA` excluído do denominador de participação."
        />
        {rent.loading ? (
          <div className="text-stone-500 text-sm">Carregando rentabilidade...</div>
        ) : rent.error ? (
          <EmptyBox testid="rent-erro">Rentabilidade indisponível.</EmptyBox>
        ) : !rent.data?.pousadas?.length ? (
          <EmptyBox testid="rent-vazio">Nenhum dado de rentabilidade no período selecionado.</EmptyBox>
        ) : (
          <div className="bg-[#FFFFFF] border border-stone-200 rounded-lg overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-stone-50 hover:bg-stone-50">
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500">#</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500">Pousada</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500 text-right">Receita líq.</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500 text-right">Custos</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500 text-right">Despesas op.</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500 text-right">Resultado</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500 text-right">Margem %</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500 text-right">Particip.</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rent.data.pousadas.map((p) => {
                  const part = participacao.data?.pousadas?.find((x) => x.pousada_id === p.pousada_id);
                  return (
                    <TableRow key={p.pousada_id || "sem_pousada"} data-testid={`rent-row-${p.pousada_id || "sem_pousada"}`}>
                      <TableCell className="text-stone-600 text-sm">{p.posicao || "—"}</TableCell>
                      <TableCell className="text-stone-900 text-sm">
                        {p.eh_sem_pousada ? <span className="text-stone-400">Sem pousada</span> : (p.pousada_nome || pousadaMap[p.pousada_id] || p.pousada_id)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{brl(p.receita_liquida)}</TableCell>
                      <TableCell className="text-right tabular-nums">{brl(p.custos_diretos)}</TableCell>
                      <TableCell className="text-right tabular-nums">{brl(p.despesas_operacionais)}</TableCell>
                      <TableCell className={`text-right tabular-nums ${Number(p.resultado_operacional || 0) >= 0 ? "text-[#083A6B]" : "text-[#D4A652]"}`}>
                        {brl(p.resultado_operacional)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-stone-700">
                        {p.margem_percentual != null ? `${Number(p.margem_percentual).toFixed(1)}%` : "—"}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-stone-700">
                        {part?.participacao_receita_liquida_pct != null
                          ? `${Number(part.participacao_receita_liquida_pct).toFixed(1)}%`
                          : (part?.participacao_receita_bruta_pct != null
                              ? `${Number(part.participacao_receita_bruta_pct).toFixed(1)}%`
                              : "—")}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            {participacao.data?.estado === "sem_dados" && (
              <div className="p-3 text-[11px] text-stone-500 border-t border-stone-200">
                Participação indisponível: denominador de receita bruta ≤ 0.
              </div>
            )}
          </div>
        )}
      </section>

      {/* Contas a Pagar (F5) */}
      <section data-testid="section-contas-pagar">
        <SectionHeader icon={Receipt}
          title="Contas a Pagar"
          subtitle="Alertas oficiais de vencimento e status financeiro (F5)."
          link="/admin/financeiro/contas-pagar" linkLabel="Abrir Contas a Pagar"
        />
        {cpResumo.loading ? (
          <div className="text-stone-500 text-sm">Carregando resumo...</div>
        ) : cpResumo.error ? (
          <EmptyBox testid="cp-resumo-erro">Resumo indisponível.</EmptyBox>
        ) : !cpResumo.data ? (
          <EmptyBox testid="cp-resumo-vazio">Sem dados.</EmptyBox>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
            <Kpi testid="kpi-cp-vencidas" label="Vencidas" tone="danger"
              valor={cpResumo.data.vencidas?.valor} sub={`${cpResumo.data.vencidas?.count ?? 0} contas`} />
            <Kpi testid="kpi-cp-hoje" label="Hoje" tone="warn"
              valor={cpResumo.data.hoje?.valor} sub={`${cpResumo.data.hoje?.count ?? 0} contas`} />
            <Kpi testid="kpi-cp-amanha" label="Amanhã" tone="warn"
              valor={cpResumo.data.amanha?.valor} sub={`${cpResumo.data.amanha?.count ?? 0} contas`} />
            <Kpi testid="kpi-cp-7d" label="Próximos 7 dias"
              valor={cpResumo.data.proximos_7?.valor} sub={`${cpResumo.data.proximos_7?.count ?? 0} contas`} />
            <Kpi testid="kpi-cp-12d" label="Próximos 12 dias"
              valor={cpResumo.data.proximos_12?.valor} sub={`${cpResumo.data.proximos_12?.count ?? 0} contas`} />
            <Kpi testid="kpi-cp-pendente" label="Pendente global"
              valor={cpResumo.data.total_pendente?.valor} sub={`${cpResumo.data.total_pendente?.count ?? 0} contas`} />
            <Kpi testid="kpi-cp-programado" label="Programado" tone="info"
              valor={cpResumo.data.total_programado?.valor} sub={`${cpResumo.data.total_programado?.count ?? 0} contas`} />
            <Kpi testid="kpi-cp-pago" label="Pago no período" tone="ok"
              valor={cpResumo.data.total_pago_periodo?.valor} sub={`${cpResumo.data.total_pago_periodo?.count ?? 0} contas`} />
          </div>
        )}
      </section>

      {/* Contas a Receber (F3) */}
      <section data-testid="section-contas-receber">
        <SectionHeader icon={HandCoins}
          title="Contas a Receber"
          subtitle="Somas neutras derivadas da listagem oficial F3 no período (por vencimento). Ativas apenas."
          link="/admin/financeiro/contas-receber" linkLabel="Abrir Contas a Receber"
        />
        {cr.loading ? (
          <div className="text-stone-500 text-sm">Carregando...</div>
        ) : cr.error ? (
          <EmptyBox testid="cr-erro">Contas a receber indisponíveis.</EmptyBox>
        ) : crTotais.count === 0 ? (
          <EmptyBox testid="cr-vazio">Nenhuma conta a receber ativa no período.</EmptyBox>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Kpi testid="kpi-cr-count" label="Contas no período" valor={0} sub={`${crTotais.count} contas`} tone="neutral" />
            <Kpi testid="kpi-cr-total" label="Valor total" valor={crTotais.total} tone="ok" />
            <Kpi testid="kpi-cr-recebido" label="Recebido" valor={crTotais.recebido} tone="ok" />
            <Kpi testid="kpi-cr-saldo" label="Saldo em aberto" valor={crTotais.saldo} tone="warn" />
          </div>
        )}
      </section>

      {/* Contas Financeiras (F4) */}
      <section data-testid="section-contas-financeiras">
        <SectionHeader icon={Landmark}
          title="Contas Financeiras"
          subtitle="Caixas, bancos e carteiras ativas. Saldo disponível é derivado por Movimentos (F6)."
          link="/admin/financeiro/contas" linkLabel="Abrir Bancos e Contas"
        />
        {cfs.loading ? (
          <div className="text-stone-500 text-sm">Carregando contas...</div>
        ) : cfs.error ? (
          <EmptyBox testid="cf-erro">Contas financeiras indisponíveis.</EmptyBox>
        ) : cfs.data.length === 0 ? (
          <EmptyBox testid="cf-vazio">
            <div className="mb-2 font-serif text-lg text-stone-800">Nenhuma conta financeira cadastrada.</div>
            <div className="text-xs text-stone-500">
              Sem contas, saldos e projeções não têm base. Cadastre a primeira em <b>Bancos e Contas</b>.
            </div>
          </EmptyBox>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {cfs.data.map((c) => (
              <div key={c.id} data-testid={`kpi-cf-${c.id}`}
                className="bg-[#FFFFFF] border border-stone-200 rounded-lg p-4">
                <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-stone-500 mb-1">
                  <Wallet size={12} className="text-[#083A6B]" /> {c.nome}
                </div>
                <div className="font-serif text-xl text-stone-900 tabular-nums">{brl(c.saldo_inicial)}</div>
                <div className="text-[11px] text-stone-500 mt-0.5">
                  {c.pousada_id ? (pousadaMap[c.pousada_id] || c.pousada_id) : "Corporativo"} · Saldo inicial
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Movimentos Financeiros (F6) — resumo do período */}
      <section data-testid="section-movimentos">
        <SectionHeader icon={BookOpen}
          title="Movimentos Financeiros"
          subtitle="Entradas e saídas efetivas no período (F6)."
          link="/admin/financeiro/movimentos" linkLabel="Abrir Livro-Caixa"
        />
        {mfs.loading ? (
          <div className="text-stone-500 text-sm">Carregando movimentos...</div>
        ) : mfs.error ? (
          <EmptyBox testid="mf-erro">Movimentos indisponíveis.</EmptyBox>
        ) : mfs.data.length === 0 ? (
          <EmptyBox testid="mf-vazio">Nenhum movimento efetivo no período.</EmptyBox>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Kpi testid="kpi-mf-entradas" label="Entradas efetivas" valor={mfTotais.entradas} tone="ok" icon={TrendingUp} />
            <Kpi testid="kpi-mf-saidas" label="Saídas efetivas" valor={mfTotais.saidas} tone="danger" icon={TrendingDown} />
            <Kpi testid="kpi-mf-resultado" label="Resultado do período"
              valor={mfTotais.resultado} tone={mfTotais.resultado >= 0 ? "ok" : "danger"} icon={ArrowRight} />
          </div>
        )}
      </section>

      {/* Projeções (F10) */}
      <section data-testid="section-projecoes">
        <SectionHeader icon={LineChart}
          title="Projeções (30 dias)"
          subtitle="Projeção oficial F10, horizonte 30 dias. Frontend não recalcula fórmula."
        />
        {projecao.loading ? (
          <div className="text-stone-500 text-sm">Carregando projeção...</div>
        ) : projecao.error ? (
          <EmptyBox testid="proj-erro">Projeção indisponível.</EmptyBox>
        ) : !projecao.data ? (
          <EmptyBox testid="proj-vazio">Sem dados de projeção.</EmptyBox>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Kpi testid="kpi-proj-saldo-base" label="Saldo base"
              valor={projecao.data.saldo_base_total} tone="neutral" />
            <Kpi testid="kpi-proj-entradas" label="Entradas previstas"
              valor={projecao.data.total_entradas_previstas} tone="ok" />
            <Kpi testid="kpi-proj-saidas" label="Saídas previstas"
              valor={projecao.data.total_saidas_previstas} tone="danger" />
            <Kpi testid="kpi-proj-menor-saldo" label="Menor saldo projetado"
              valor={projecao.data.menor_saldo_projetado}
              sub={projecao.data.data_menor_saldo ? `Em ${fmtDate(projecao.data.data_menor_saldo)}` : undefined}
              tone={projecao.data.risco_caixa ? "danger" : "warn"} />
            {projecao.data.risco_caixa && (
              <div className="col-span-2 sm:col-span-4 bg-[#F5E4DD] text-[#7c3a20] border border-[#D4A652]/25 rounded-lg px-3 py-2 text-sm flex items-center gap-2">
                <AlertTriangle size={14} /> Risco de caixa identificado pelo backend (F10).
              </div>
            )}
          </div>
        )}
      </section>

      {/* Podemos Investir? (F11) */}
      <section data-testid="section-investimento">
        <SectionHeader icon={Lightbulb}
          title="Podemos Investir?"
          subtitle="Análise conservadora oficial F11, horizonte 90 dias. Resultado é descritivo (não é recomendação)."
        />
        {investir.loading ? (
          <div className="text-stone-500 text-sm">Carregando análise...</div>
        ) : investir.error ? (
          <EmptyBox testid="pi-erro">Análise indisponível.</EmptyBox>
        ) : !investir.data ? (
          <EmptyBox testid="pi-vazio">Sem dados.</EmptyBox>
        ) : (
          <div className="bg-[#FFFFFF] border border-stone-200 rounded-lg p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <div className="text-[11px] uppercase text-stone-500">Decisão do backend</div>
                <div className={`font-serif text-xl ${investir.data.pode_investir ? "text-[#083A6B]" : "text-[#D4A652]"}`}>
                  {investir.data.pode_investir ? "Podemos investir" : "Não podemos investir"}
                </div>
                {investir.data.motivo && (
                  <div className="text-[11px] text-stone-500 mt-1">{investir.data.motivo}</div>
                )}
              </div>
              <div>
                <div className="text-[11px] uppercase text-stone-500">Máximo investível (conservador)</div>
                <div className="font-serif text-xl tabular-nums">
                  {brl(investir.data.maximo_investivel_conservador ?? investir.data.maximo_investivel ?? 0)}
                </div>
              </div>
              <div>
                <div className="text-[11px] uppercase text-stone-500">Menor saldo projetado (F10)</div>
                <div className="font-serif text-xl tabular-nums">
                  {brl(investir.data.menor_saldo_projetado ?? investir.data.projecao?.menor_saldo_projetado ?? 0)}
                </div>
                {(investir.data.data_menor_saldo || investir.data.projecao?.data_menor_saldo) && (
                  <div className="text-[11px] text-stone-500 mt-0.5">
                    Em {fmtDate(investir.data.data_menor_saldo || investir.data.projecao?.data_menor_saldo)}
                  </div>
                )}
              </div>
            </div>
            <div className="mt-3 text-[11px] text-stone-500">
              Horizonte: 90 dias · Análise oficial F11. Nenhum score, nenhum ranking, nenhuma recomendação de investimento gerada pelo frontend.
            </div>
          </div>
        )}
      </section>

      <div className="mt-10 text-[11px] text-stone-500">
        Dashboard 100% derivado (F13.6). Sem escrita, sem materialização, sem duplicação de regra financeira.
        Fontes: F2, F3, F4, F5, F6, F10, F11, F12.1/F12.2, F12.3.
      </div>
    </div>
  );
}
